// API Configuration for RussAdvisor Azure Functions
// Replace these URLs with your actual Azure Functions endpoints once deployed

const API_CONFIG = {
  // Base URL for your Azure Functions
  // Example: 'https://russadvisor-func.azurewebsites.net/api'
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:7071/api',
  
  endpoints: {
    // Dashboard data
    dashboard: '/dashboard',

    // Portfolio endpoints
    portfolio: '/portfolio',
    portfolioHistory: '/portfolio/history',

    // Schwab connection health (Settings page).
    // GET  /schwab/status -> token age, days until the 7-day reauth cap,
    //      last portfolio sync time, per-account snapshot detail.
    // POST /schwab/sync   -> runs a sync now; 200 { ok, accountsSynced }
    //      or 500 { ok: false, error } when the authorization has lapsed.
    // NOT under /admin/* -- the Functions runtime reserves that path segment
    // and silently drops any function route starting with it.
    schwabStatus: '/schwab/status',
    schwabSync: '/schwab/sync',
    // Anonymous route -- the OAuth handshake redirects here, so it's a plain
    // link rather than an apiCall.
    schwabAuth: '/schwab-auth',

    cryptoPortfolio: '/crypto/portfolio',

    // Crypto holdings management. Distinct from /crypto/portfolio: that one is
    // the display shape (rolled up, dust-filtered); these are the editing shape
    // and hide nothing, because cleaning up dust is the whole point.
    // GET    /crypto/holdings          -> { holdings, dustThreshold, lastUpdated }
    // POST   /crypto/holdings          -> upsert by symbol { symbol, quantity, costBasis }
    // DELETE /crypto/holdings/{symbol} -> 204, or 404 if not held
    cryptoHoldings: '/crypto/holdings',
    cryptoHoldingBySymbol: (symbol) => `/crypto/holdings/${encodeURIComponent(symbol)}`,

    // Daily report endpoints (V2 structuredOutput shape; see backend Track F.6)
    // GET /reports?count=N -> List<DailyReport>, sorted by reportDate desc
    reports: '/reports',
    reportsWithCount: (count) => `/reports?count=${count}`,

    // Transcripts (Track E backend, browse follow-up).
    // POST /transcripts with {title, rawText} -> 202 Accepted with transcriptId.
    // GET  /transcripts?count=N -> List<TranscriptListItem> sorted uploadedAt desc.
    // Note: same path, distinguished by HTTP method.
    transcripts: '/transcripts',
    transcriptsWithCount: (count) => `/transcripts?count=${count}`,
    transcriptById: (id) => `/transcripts/${encodeURIComponent(id)}`,

    // Telegram messages endpoints
    messages: '/messages',
    messagesByCategory: (category) => `/messages?category=${category}`,
    messagesWithParams: (params) => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page);
      if (params.pageSize) searchParams.set('pageSize', params.pageSize);
      if (params.startDate) searchParams.set('startDate', params.startDate);
      if (params.endDate) searchParams.set('endDate', params.endDate);
      if (params.search) searchParams.set('search', params.search);
      if (params.onlyRelevant !== undefined) searchParams.set('onlyRelevant', params.onlyRelevant);
      const queryString = searchParams.toString();
      return queryString ? `/messages?${queryString}` : '/messages';
    },

    // Chat attachments. Uploaded separately from the message because the send
    // route streams its response -- multipart in / SSE out on one request makes
    // upload progress impossible to report.
    // GET    /chat/attachment-limits                              -> effective ceilings
    // POST   /chat/conversations/{id}/attachments                 -> multipart, returns metadata
    // DELETE /chat/conversations/{id}/attachments/{attachmentId}  -> discard before sending
    // GET    /chat/conversations/{id}/attachments/{aId}/content   -> bytes, for thumbnails
    chatAttachmentLimits: '/chat/attachment-limits',
    chatAttachments: (conversationId) =>
      `/chat/conversations/${encodeURIComponent(conversationId)}/attachments`,
    chatAttachmentById: (conversationId, attachmentId) =>
      `/chat/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`,
    chatAttachmentContent: (conversationId, attachmentId) =>
      `/chat/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/content`,

    // Settings endpoints (UserSettings page).
    // GET  /settings -> UserSettings JSON; returns defaults if none saved yet.
    // POST /settings  -> upserts the body, returns the saved doc with UpdatedAt stamped.
    settings: '/settings',

    // Browser push (Settings -> Push Notifications). The service worker that
    // shows the notifications is public/push-sw.js.
    // GET    /notifications/push/config             -> { configured, publicKey }
    // GET    /notifications/push/subscriptions      -> [{ id, label, endpoint, createdAt, lastSuccessAt }]
    // POST   /notifications/push/subscriptions      -> PushSubscription.toJSON() + { label }; idempotent
    // DELETE /notifications/push/subscriptions/{id} -> 204, or 404 if unknown
    // POST   /notifications/push/test               -> { delivered, failed, removed, skippedReason }
    pushConfig: '/notifications/push/config',
    pushSubscriptions: '/notifications/push/subscriptions',
    pushSubscriptionById: (id) => `/notifications/push/subscriptions/${encodeURIComponent(id)}`,
    pushTest: '/notifications/push/test',

    // Model catalog for the Settings model pickers. Live list from the
    // Anthropic Models API (cached server-side), independent of the chat feature.
    // GET /models -> { models: [{ id, name, description, inputPerMillion, outputPerMillion }] }
    models: '/models',
    // GET /model-tasks -> { tasks: [{ key, label, group }] } -- the configurable
    // AI decision points rendered as model pickers in Settings.
    modelTasks: '/model-tasks',

    // Tickers + Themes browser (Mentions page). Joins the V2 tickers/themes
    // registries with daily-report callsToAction/highlights/alerts and
    // transcript extractedTickers/extractedThemes.
    tickers: '/tickers',
    tickersWithLimit: (limit) => `/tickers?limit=${limit}`,
    tickerById: (symbol) => `/tickers/${encodeURIComponent(symbol)}`,
    /**
     * Price history for a single ticker. range: day|week|month|ytd. Backend
     * tries Schwab first (stocks/ETFs), falls back to CoinGecko (crypto).
     * 404 when neither provider has data; frontend hides the chart in that case.
     */
    tickerPrices: (symbol, range = 'month') =>
      `/tickers/${encodeURIComponent(symbol)}/prices?range=${encodeURIComponent(range)}`,

    // Watchlist (single-doc; persists tickers + themes the user wants to track).
    // GET    /watchlist                  -> Watchlist doc
    // POST   /watchlist                  -> add entry { kind, key, label? } (idempotent)
    // DELETE /watchlist/{kind}/{key}     -> remove entry
    // GET    /watchlist/insights         -> watchlist + recent conviction-change list
    watchlist:                () => '/watchlist',
    watchlistEntry: (kind, key) => `/watchlist/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`,
    watchlistInsights:        () => '/watchlist/insights',

    // Security identities -- the curated alias map that lets one company be
    // reached by every symbol it trades under. Without it, a Lanxess ADR held
    // as LNXSY never joins Russ's posts about LXS / LXS.DE and its detail page
    // 404s.
    // GET    /securities                             -> SecurityIdentity[]
    // GET    /unmatched-securities                   -> symbols no identity claims
    //        (not /securities/unmatched: the host routed that to /securities/{canonical})
    // GET    /securities/{canonical}                 -> SecurityIdentity
    // PUT    /securities/{canonical}                 -> create / edit displayName etc.
    // DELETE /securities/{canonical}
    // POST   /securities/{canonical}/aliases         -> { value, kind, exchange }
    // DELETE /securities/{canonical}/aliases/{value}
    // NOT under /admin/* -- the Functions runtime reserves that path segment.
    securities:               () => '/securities',
    securitiesUnmatched:      () => '/unmatched-securities',
    securityByCanonical: (canonical) => `/securities/${encodeURIComponent(canonical)}`,
    securityAliases: (canonical) => `/securities/${encodeURIComponent(canonical)}/aliases`,
    securityAlias: (canonical, value) =>
      `/securities/${encodeURIComponent(canonical)}/aliases/${encodeURIComponent(value)}`,
    themes: '/themes',
    themesWithLimit: (limit) => `/themes?limit=${limit}`,
    themeBySlug: (slug) => `/themes/${encodeURIComponent(slug)}`,

    // Call scorecard -- Russ's trade plans (kind=plan) and daily-report
    // guidance with a target (kind=guidance) tracked against actual prices.
    // GET  /calls?status=open|closed&kind=plan|guidance&symbol=&limit=
    // GET  /calls/summary?kind=&symbol=         -> stats + byKind/byHorizon/bySymbol
    // POST /calls/evaluate[?symbol=]            -> run the evaluator now
    // POST /calls/rebuild[?reset=true]          -> rebuild from stored plans + reports
    calls: (params = {}) => withQuery('/calls', params),
    callsSummary: (params = {}) => withQuery('/calls/summary', params),
    callsEvaluate: (symbol) => withQuery('/calls/evaluate', { symbol }),
    callsRebuild: (reset) => withQuery('/calls/rebuild', { reset: reset ? 'true' : undefined }),

    // Market regime -- Russ's morning macro checklist read off Schwab + FRED.
    // GET  /market-regime?days=120   -> { snapshot, history: [{date, indicatorValues}], definitions }
    // POST /market-regime/refresh    -> recompute the trailing two weeks now
    // POST /market-regime/rebuild?days=365 -> backfill history
    marketRegime: (days) => withQuery('/market-regime', { days }),
    marketRegimeRefresh: '/market-regime/refresh',
    marketRegimeRebuild: (days) => withQuery('/market-regime/rebuild', { days }),

    // Chat endpoints
    chatConversations: '/chat/conversations',
    chatConversationById: (id) => `/chat/conversations/${id}`,
    chatMessages: (conversationId) => `/chat/conversations/${conversationId}/messages`,
    chatMessagesStream: (conversationId) => `/chat/conversations/${conversationId}/messages/stream`,
    chatModels: '/chat/models',

    // Health check
    health: '/health'
  }
};

/** Append only the defined, non-empty params as a query string. */
const withQuery = (path, params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') search.set(k, v);
  });
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

// Helper function to build full URL
export const buildUrl = (endpoint) => {
  return `${API_CONFIG.baseUrl}${endpoint}`;
};

/**
 * Authenticated multipart upload.
 *
 * Deliberately not routed through apiCall: that helper sets a JSON
 * Content-Type, and setting any Content-Type on a FormData body stops the
 * browser writing the multipart boundary, which the server then can't parse.
 * The browser must be left to set it.
 */
export const apiUpload = async (endpoint, files) => {
  const url = buildUrl(endpoint);
  const functionKey = localStorage.getItem('azure_function_key');

  const formData = new FormData();
  for (const file of files) {
    formData.append('file', file, file.name);
  }

  const headers = {};
  if (functionKey) {
    headers['x-functions-key'] = functionKey;
  }

  const response = await fetch(url, { method: 'POST', headers, body: formData });

  if (!response.ok) {
    // The backend returns a specific reason (which file, which limit) and it's
    // the only useful thing to show the user, so surface it rather than a
    // status code.
    let message = `Upload failed: ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body; keep the status text */
    }
    throw new Error(message);
  }

  return response.json();
};

// Helper function for authenticated API calls
export const apiCall = async (endpoint, options = {}) => {
  try {
    const url = buildUrl(endpoint);
    
    // Get function key from localStorage
    const functionKey = localStorage.getItem('azure_function_key');
    
    // Build headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    // Add function key if available
    if (functionKey) {
      headers['x-functions-key'] = functionKey;
    }
    
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Prefer the server's own { error } message over a bare statusText --
      // endpoints like POST /securities/{c}/aliases return a 409 explaining
      // exactly which other identity already owns the alias, and that text is
      // the whole value of the response.
      let detail = response.statusText;
      try {
        const body = await response.json();
        if (body?.error) detail = body.error;
      } catch {
        // Non-JSON body; statusText is all we have.
      }
      const err = new Error(`API call failed: ${detail}`);
      err.status = response.status;
      throw err;
    }

    // Handle 204 No Content responses (e.g., DELETE operations)
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Helper function for streaming API calls (Server-Sent Events)
export const apiCallStreaming = async (endpoint, options = {}, onEvent) => {
  const url = buildUrl(endpoint);

  // Get function key from localStorage
  const functionKey = localStorage.getItem('azure_function_key');

  // Build headers with authentication
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add function key if available
  if (functionKey) {
    headers['x-functions-key'] = functionKey;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`API call failed: ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    // Process complete SSE events from buffer
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const eventData = JSON.parse(line.slice(6));
          onEvent(eventData);
        } catch (e) {
          console.warn('Failed to parse SSE event:', line);
        }
      }
    }
  }

  // Process any remaining data in buffer
  if (buffer.startsWith('data: ')) {
    try {
      const eventData = JSON.parse(buffer.slice(6));
      onEvent(eventData);
    } catch (e) {
      // Ignore incomplete final event
    }
  }
};

export default API_CONFIG;
